import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Status for each dimension in the generation job
 */
export const DimensionStatusSchema = z.enum(["completed", "error"]);
export type DimensionStatus = z.infer<typeof DimensionStatusSchema>;

/**
 * Overall generation job status
 */
export const GenerationJobStatusSchema = z.enum([
  "pending",
  "completed",
  "error",
  "archived",
  "implemented",
]);
export type GenerationJobStatus = z.infer<typeof GenerationJobStatusSchema>;

/**
 * GA4 Insights Generation Job schema
 * Tracks the progress of insight generation across all dimensions
 *
 * Document path: organizations/{orgId}/domains/{domainId}/pages/{pageId}/integrations/google-analytics/jobs/{generation_run_id}
 */
export const GA4InsightsGenerationJobSchema = z.object({
  // Document identifier (UUID generation_run_id)
  id: z.string(),
  generation_run_id: z.string().nullable().default(null),

  // Reference IDs
  user_id: z.string(),
  domain_id: z.string(),
  page_id: z.string(),

  // Overall status
  status: GenerationJobStatusSchema.default("pending"),

  // Progress tracking
  total_dimensions: z.number().default(7),
  completed_dimensions: z.number().default(0),

  // Per-dimension status (only populated when dimension completes or errors)
  dimensions_status: z.record(z.string(), z.string()).default({}),

  // Timestamps
  created_at: RequiredFirestoreTimestampSchema.nullable().default(null),
  updated_at: RequiredFirestoreTimestampSchema.nullable().default(null),
  completed_at: RequiredFirestoreTimestampSchema.nullable().default(null),

  // Error handling
  error_message: z.string().default(""),
  dimension_errors: z.record(z.string(), z.string()).default({}),

  // Summary statistics
  total_insights_generated: z.number().default(0),
  total_processing_time_seconds: z.number().nullable().default(null),
});

export type GA4InsightsGenerationJob = z.infer<
  typeof GA4InsightsGenerationJobSchema
>;

/**
 * Dimensions tracked by the generation job
 */
export const GA4_DIMENSIONS = [
  "sessionSourceMedium",
  "country",
  "deviceCategory",
  "firstUserDefaultChannelGroup",
  "landingPagePlusQueryString",
  "sessionCampaignName",
  "browser",
] as const;

export type GA4Dimension = (typeof GA4_DIMENSIONS)[number];

/**
 * Parse function for GA4InsightsGenerationJob
 * Throws on validation failure
 */
export const parseGA4InsightsGenerationJob = (
  data: unknown
): GA4InsightsGenerationJob => {
  return GA4InsightsGenerationJobSchema.parse(data);
};

/**
 * Safe parse function for GA4InsightsGenerationJob
 * Returns a result object with success status and data/error
 */
export const safeParseGA4InsightsGenerationJob = (data: unknown) => {
  return GA4InsightsGenerationJobSchema.safeParse(data);
};
